import { sprite, type SpriteDef } from './types';

// Machine face on the 64×24 amber LED matrix (docs/07).
// '#' lit LED, 'e' eye interior (pupil travel area), 'p' pupil (centered gaze; renderer shifts it),
// 'r' red LED accent, '.' unlit LED. Eye positions are shared so the blink overlay lines up.
const FACE_COLORS = { '#': 'brass', p: 'gold', e: 'aubergine', r: 'red' } as const;

function face(name: string, rows: string[]): SpriteDef {
  return sprite(`face_${name}`, { ...FACE_COLORS }, rows);
}

export const FACE_SPRITES: SpriteDef[] = [
  face('neutral', [
    '................................................................',
    '................................................................',
    '................................................................',
    '.................########..............########.................',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '..............#eeeeppppeeee#........#eeeeppppeeee#..............',
    '.............#eeeeeppppeeeee#......#eeeeeppppeeeee#.............',
    '..............#eeeeppppeeee#........#eeeeppppeeee#..............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '.................########..............########.................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '........................################........................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
  ]),
  face('pleased', [
    '................................................................',
    '................................................................',
    '................................................................',
    '.................########..............########.................',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '..............#eeeeppppeeee#........#eeeeppppeeee#..............',
    '..............#eeeeppppeeee#........#eeeeppppeeee#..............',
    '.............#eeeeeeeeeeeeee#......#eeeeeeeeeeeeee#.............',
    '..............#eeee####eeee#........#eeee####eeee#..............',
    '..............#ee##....##ee#........#ee##....##ee#..............',
    '...............##........##..........##........##...............',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '........................###..........###........................',
    '...........................##......##...........................',
    '.............................######.............................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
  ]),
  face('laughing', [
    '................................................................',
    '................................................................',
    '................................................................',
    '....................#.....................#.....................',
    '...................###...................###....................',
    '..................##.##.................##.##...................',
    '.................##...##...............##...##..................',
    '................##.....##.............##.....##.................',
    '...............##.......##...........##.......##................',
    '..............##.........##.........##.........##...............',
    '..............#...........#.........#...........#...............',
    '................................................................',
    '................................................................',
    '................................................................',
    '.....................######################.....................',
    '......................#..................#......................',
    '......................#..................#......................',
    '......................#..................#......................',
    '.......................#................#.......................',
    '........................#..............#........................',
    '.........................##..######..##.........................',
    '...........................##########...........................',
    '...........................##########...........................',
    '...........................##########...........................',
  ]),
  face('sulking', [
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '.............################......################.............',
    '.............###pppp#########......###pppp#########.............',
    '..............#eppppeeeeeee#........#eppppeeeeeee#..............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '.................########..............########.................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '............................########............................',
    '..........................##........##..........................',
    '.........................#............#.........................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
  ]),
  face('hungry', [
    '................................................................',
    '................................................................',
    '................................................................',
    '.................########..............########.................',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '.............#eeeeeeeeeeeeee#......#eeeeeeeeeeeeee#.............',
    '..............#eeeeppppeeee#........#eeeeppppeeee#..............',
    '..............#eeeeppppeeee#........#eeeeppppeeee#..............',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '.................########..............########.................',
    '................................................................',
    '................................................................',
    '................................................................',
    '...........................##########...........................',
    '.........................##..........##.........................',
    '........................#..............#........................',
    '.......................#................#.......................',
    '........................#..............#........................',
    '.........................##..........##.........................',
    '...........................##########...........................',
    '....................................##..........................',
    '....................................##..........................',
  ]),
  face('ravenous', [
    '................................................................',
    '................#######..................#######................',
    '......................##................##......................',
    '.................########..............########.................',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '.............#eeeeeppppeeeee#......#eeeeeppppeeeee#.............',
    '..............#eeeeppppeeee#........#eeeeppppeeee#..............',
    '..............#eeeeppppeeee#........#eeeeppppeeee#..............',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '.................########..............########.................',
    '................................................................',
    '..........................############..........................',
    '.......................###.#........#.###.......................',
    '......................#.#..#........#..#.#......................',
    '.....................#..#..............#..#.....................',
    '....................#......................#....................',
    '.....................#....................#.....................',
    '......................#.......rrrr.......#......................',
    '.......................###...rrrrrr...###.......................',
    '..........................############..........................',
    '...........................#........#...........................',
    '...........................#....................................',
  ]),
  face('suspicious', [
    '................................................................',
    '................................................................',
    '................................................................',
    '.......................................########.................',
    '.....................................##eeeeeeee##...............',
    '............#################.......#eeeeeeeeeeee#..............',
    '..............#eeeeeppppeee#........#eeeeeppppeee#..............',
    '.............#eeeeeeppppeeee#......#eeeeeeppppeeee#.............',
    '..............#eeeeeppppeee#........#eeeeeppppeee#..............',
    '..............##############........#eeeeeeeeeeee#..............',
    '.....................................##eeeeeeee##...............',
    '.......................................########.................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '.........................................##.....................',
    '..........................###############.......................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
  ]),
  face('adoring', [
    '................................................................',
    '................####..####............####..####................',
    '...............############..........############...............',
    '...............############..........############...............',
    '...............############..........############...............',
    '...............############..........############...............',
    '...............############..........############...............',
    '...............############..........############...............',
    '................##########............##########................',
    '.................########..............########.................',
    '.................########..............########.................',
    '...................####..................####...................',
    '....................##....................##....................',
    '................................................................',
    '................................................................',
    '................................................................',
    '.........................##..........##.........................',
    '...........................##......##...........................',
    '.............................######.............................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
  ]),
  face('surprised', [
    '................................................................',
    '................................................................',
    '.................########..............########.................',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '.............#eeeeeeeeeeeeee#......#eeeeeeeeeeeeee#.............',
    '.............#eeeeeeeppeeeee#......#eeeeeeeppeeeee#.............',
    '.............#eeeeeeeppeeeee#......#eeeeeeeppeeeee#.............',
    '.............#eeeeeeeeeeeeee#......#eeeeeeeeeeeeee#.............',
    '.............#eeeeeeeeeeeeee#......#eeeeeeeeeeeeee#.............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '.................########..............########.................',
    '................................................................',
    '................................................................',
    '................................................................',
    '...............................##...............................',
    '..............................#..#..............................',
    '.............................#....#.............................',
    '.............................#....#.............................',
    '..............................#..#..............................',
    '...............................##...............................',
    '................................................................',
    '................................................................',
  ]),
  face('sleeping', [
    '...........................................................###..',
    '............................................................#...',
    '...........................................................###..',
    '................................................................',
    '.....................................................#####......',
    '........................................................#.......',
    '.......................................................#........',
    '..............#............#........#............#....#.........',
    '...............###......###..........###......###....#####......',
    '..................######................######..................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '............................########............................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
  ]),
  face('starving', [
    '.............####..............................####.............',
    '.................#####....................#####.................',
    '................................................................',
    '.................########..............########.................',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '..............#eeeeeerreeee#........#eeeeeerreeee#..............',
    '.............#eeeeeeerreeeee#......#eeeeeeerreeeee#.............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '.................########..............########.................',
    '................................................................',
    '................................................................',
    '................................................................',
    '........................################........................',
    '.....................###.###.###.###.######.....................',
    '....................#.#...#...#...#...#...##....................',
    '...................#..........rrrr..........#...................',
    '...................#.........rrrrrr.........#...................',
    '....................#...#...#...#...#...#..#....................',
    '.....................#####.###.###.###.####.....................',
    '........................################........................',
    '................................................................',
  ]),
  face('devouring', [
    '................................................................',
    '................................................................',
    '...................####..................####...................',
    '................###eeee###............###eeee###................',
    '..............##eeepppeeee##........##eeepppeeee##..............',
    '..............#eeeepppeeeee#........#eeeepppeeeee#..............',
    '..............#eeeepppeeeee#........#eeeepppeeeee#..............',
    '...............##eeeeeeee##..........##eeeeeeee##...............',
    '.................########..............########.................',
    '................................................................',
    '................................................................',
    '.......##################################################.......',
    '.......####..###..###..###..###..###..###..###..###..####.......',
    '.......#.#....#....#....#....#....#....#....#....#....#.#.......',
    '........##....#....#....#....#....#....#....#....#....##........',
    '........#..............................................#........',
    '.........##...#..................................#...##.........',
    '..........#...#..............rrrrrr..............#...#..........',
    '...........####....#..........rrrr..........#....####...........',
    '.............###...#....#..............#....#...###.............',
    '...............##.##....#....#....#....#....##.##...............',
    '.................####..##....#....#....##..####.................',
    '....................######..###..###..######....................',
    '.........................##############.........................',
  ]),
  face('blink', [
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '.............################......################.............',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
  ]),
];
